/**
 * ============================================
 * REQUIRE SIZE SELECTION (MeshPlanet)
 * PURPOSE: Customers must actively pick a size before adding to cart.
 *          Horizon always keeps a variant selected (the first available one),
 *          so instead of fighting its variant logic we:
 *            1. Style the default size as unselected while the size fieldset
 *               has [data-mp-size-pending] (CSS in variant-main-picker-toous).
 *            2. Block the add-to-cart click (main + sticky bar) and show an
 *               error until the customer clicks/changes a size.
 *          Variant updates re-render (morph) the picker from server HTML, so a
 *          MutationObserver re-applies the pending state after each update.
 *          The size always starts unselected, even with ?variant= in the URL
 *          (client request).
 * USED BY: blocks/variant-picker-toous.liquid ("Require size selection")
 * ADDED: 2026-10-20 by MeshPlanet
 * ============================================
 */
(() => {
  // The block can render this script more than once (e.g. theme editor)
  if (window.mpSizeRequiredInitialized) return;
  window.mpSizeRequiredInitialized = true;

  const SIZE_FIELDSET = '[data-mp-size-option]';
  const PENDING_ATTR = 'data-mp-size-pending';
  const ERROR_ATTR = 'data-mp-size-error';
  // Quick-add overlays reuse the picker markup but don't load this script
  const EXCLUDED_CONTEXT = '.quick-add-variants-overlay, product-card, quick-add-dialog';
  // Same scope Horizon's product form uses to pair a picker with its buttons
  const SCOPE = '.shopify-section, dialog';

  /** Scopes (by key) where the customer already picked a size */
  const chosenScopes = new Set();

  /** @param {Element} element */
  const getScopeKey = (element) => {
    const scope = element.closest(SCOPE);
    return scope?.id || 'page';
  };

  /** @param {Element} fieldset */
  const isManaged = (fieldset) => !fieldset.closest(EXCLUDED_CONTEXT);

  const getManagedFieldsets = (root = document) =>
    Array.from(root.querySelectorAll(SIZE_FIELDSET)).filter(isManaged);

  /**
   * Keeps every size fieldset in sync with the in-memory state. Idempotent, so
   * it is safe to call from the MutationObserver (attributes only change when
   * they differ, which stops the observer from looping).
   */
  const sync = () => {
    for (const fieldset of getManagedFieldsets()) {
      const isChosen = chosenScopes.has(getScopeKey(fieldset));

      if (isChosen && fieldset.hasAttribute(PENDING_ATTR)) {
        fieldset.removeAttribute(PENDING_ATTR);
        fieldset.removeAttribute(ERROR_ATTR);
      } else if (!isChosen && !fieldset.hasAttribute(PENDING_ATTR)) {
        fieldset.setAttribute(PENDING_ATTR, '');
      }
    }
  };

  /**
   * A click on any size (including the default one, which fires no "change")
   * or a keyboard change marks the size as chosen.
   * @param {Event} event
   */
  const handleSizeInteraction = (event) => {
    if (!(event.target instanceof Element)) return;
    const fieldset = event.target.closest(SIZE_FIELDSET);
    if (!fieldset || !isManaged(fieldset) || !(event.target instanceof HTMLInputElement)) return;

    chosenScopes.add(getScopeKey(fieldset));
    sync();
  };

  document.addEventListener('click', handleSizeInteraction);
  document.addEventListener('change', handleSizeInteraction);

  /**
   * Returns the pending size fieldset paired with the given element, if any.
   * @param {Element} element
   */
  const getPendingFieldset = (element) => {
    if (element.closest(EXCLUDED_CONTEXT)) return null;
    const scope = element.closest(SCOPE);
    if (!scope || chosenScopes.has(getScopeKey(element))) return null;
    return getManagedFieldsets(scope).find((fieldset) => fieldset.hasAttribute(PENDING_ATTR)) || null;
  };

  /** @param {Element} fieldset */
  const showError = (fieldset) => {
    fieldset.setAttribute(ERROR_ATTR, '');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    fieldset.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });

    const firstInput = fieldset.querySelector('input:checked, input');
    if (firstInput instanceof HTMLElement) firstInput.focus({ preventScroll: true });
  };

  /**
   * Capture phase on window runs before Horizon's delegated document
   * listeners (on:click / on:submit), so the cart request and the fly-to-cart
   * animation never start. Covers the main button and the sticky bar button.
   * @param {Event} event
   */
  const blockAddToCart = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger =
      event.type === 'submit'
        ? event.target.closest('product-form-component')
        : event.target.closest('[ref="addToCartButton"]');
    if (!trigger) return;

    const pendingFieldset = getPendingFieldset(trigger);
    if (!pendingFieldset) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showError(pendingFieldset);
  };

  window.addEventListener('click', blockAddToCart, true);
  window.addEventListener('submit', blockAddToCart, true);

  // Re-apply the state after Horizon morphs the picker on variant changes
  const observer = new MutationObserver(sync);
  observer.observe(document.querySelector('main') || document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [PENDING_ATTR],
  });
})();
